import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { useTranslation } from 'react-i18next';

interface Slide {
  id: number;
  title: string;
  subtitle: string;
  description: string;
  image_url: string;
  button_text?: string;
  button_link?: string;
  is_active: boolean;
  order: number;
}

interface SliderSettings {
  slider_width: string;
  slider_height: string;
  content_position: string;
  text_alignment: string;
  transition_effect: string;
  transition_duration: number;
  auto_play: boolean;
  auto_play_delay: number;
  custom_css_classes: string;
  slider_id: string;
}

const HeroSlider: React.FC = () => {
  const [slides, setSlides] = useState<Slide[]>([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sliderSettings, setSliderSettings] = useState<SliderSettings>({
    slider_width: 'full',
    slider_height: 'auto',
    content_position: 'center',
    text_alignment: 'center',
    transition_effect: 'fade',
    transition_duration: 500,
    auto_play: true,
    auto_play_delay: 5000,
    custom_css_classes: '',
    slider_id: 'main-hero-slider'
  });

  useEffect(() => {
    fetchSlides();
    fetchSliderSettings();
  }, []);

  useEffect(() => {
    if (slides.length > 0 && sliderSettings.auto_play) {
      const timer = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
      }, sliderSettings.auto_play_delay);

      return () => clearInterval(timer);
    }
  }, [slides.length, sliderSettings.auto_play, sliderSettings.auto_play_delay]);

  const fetchSlides = async () => {
    try {
      const response = await api.get('/cms/slides');
      const activeSlides = response.data.filter((slide: Slide) => slide.is_active);
      console.log('Fetched slides:', activeSlides);
      setSlides(activeSlides);
    } catch (error) {
      console.error('Error fetching slides:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSliderSettings = async () => {
    try {
      const response = await api.get('/cms/slider-settings');
      if (response.data) {
        console.log('Fetched slider settings:', response.data);
        setSliderSettings(response.data);
      }
    } catch (error) {
      console.error('Error fetching slider settings:', error);
    }
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  const goToPrevious = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const goToNext = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="hero-slider loading">
        <div className="loading-spinner">{t('hero.loading')}</div>
      </div>
    );
  }

  if (slides.length === 0) {
    return (
      <div className="hero-slider">
        <div className="slide default-slide">
          <div className="slide-content">
            <h1>{t('hero.fallbackTitle')}</h1>
            <p>{t('hero.fallbackSubtitle')}</p>
            <button className="cta-button">{t('hero.fallbackCTA')}</button>
          </div>
        </div>
      </div>
    );
  }

  // Generate dynamic styles based on settings
  const getSliderStyles = () => {
    const styles: React.CSSProperties = {
      width: sliderSettings.slider_width === 'full' ? '100vw' : 
             sliderSettings.slider_width === 'container' ? '100%' : 
             sliderSettings.slider_width,
      height: sliderSettings.slider_height === 'auto' ? 'auto' : 
              sliderSettings.slider_height,
      transition: `all ${sliderSettings.transition_duration}ms ease-in-out`,
      padding: 0,
      margin: 0,
      maxWidth: 'none',
    };
    return styles;
  };

  const getSlideContentStyles = () => {
    const positionMap: { [key: string]: string } = {
      'center': 'center center',
      'left': 'left center',
      'right': 'right center',
      'top-left': 'left top',
      'top-right': 'right top',
      'bottom-left': 'left bottom',
      'bottom-right': 'right bottom',
    };

    return {
      justifyContent: positionMap[sliderSettings.content_position]?.split(' ')[0] || 'center',
      alignItems: positionMap[sliderSettings.content_position]?.split(' ')[1] || 'center',
      textAlign: sliderSettings.text_alignment as 'left' | 'center' | 'right',
    };
  };

  const getTransitionClass = () => {
    return `transition-${sliderSettings.transition_effect}`;
  };

  console.log('Current slide:', currentSlide, 'Total slides:', slides.length);
  console.log('Slider settings:', sliderSettings);

  return (
    <div 
      className={`hero-slider ${getTransitionClass()} ${sliderSettings.custom_css_classes}`}
      style={getSliderStyles()}
      id={sliderSettings.slider_id}
    >
      <div className="slider-container">
        {slides.map((slide, index) => {
          const isActive = index === currentSlide;
          const slideStyle = {
            backgroundImage: `url("${slide.image_url}")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundAttachment: 'scroll',
            transitionDuration: `${sliderSettings.transition_duration}ms`,
            backgroundColor: '#f8f9fa', // Fallback background color
          };
          
          console.log(`Slide ${index}:`, {
            isActive,
            imageUrl: slide.image_url,
            style: slideStyle
          });

          // Test image loading
          const testImg = new Image();
          testImg.onload = () => console.log(`✅ Image loaded successfully for slide ${index}:`, slide.image_url);
          testImg.onerror = () => console.error(`❌ Failed to load image for slide ${index}:`, slide.image_url);
          testImg.src = slide.image_url;
          
          return (
            <div
              key={slide.id}
              className={`slide ${isActive ? 'active' : ''}`}
              style={slideStyle}
              onError={(e) => {
                console.error(`Failed to load background image for slide ${index}:`, slide.image_url);
                console.error('Error event:', e);
              }}
            >
            <div className="slide-overlay"></div>
            <div className="slide-content" style={getSlideContentStyles()}>
              <h1 className="slide-title">{slide.title}</h1>
              <h2 className="slide-subtitle">{slide.subtitle}</h2>
              <p className="slide-description">{slide.description}</p>
              {slide.button_text && slide.button_link && (
                <a 
                  href={slide.button_link} 
                  className="cta-button"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {slide.button_text}
                </a>
              )}
            </div>
          </div>
          );
        })}
      </div>

      {/* Navigation Arrows */}
      <button className="slider-nav prev" onClick={goToPrevious}>
        <i className="fas fa-chevron-left"></i>
      </button>
      <button className="slider-nav next" onClick={goToNext}>
        <i className="fas fa-chevron-right"></i>
      </button>

      {/* Dots Indicator */}
      <div className="slider-dots">
        {slides.map((_, index) => (
          <button
            key={index}
            className={`dot ${index === currentSlide ? 'active' : ''}`}
            onClick={() => goToSlide(index)}
          />
        ))}
      </div>
    </div>
  );
};

export default HeroSlider;
